<script setup lang="ts">
import type { ComboboxContentEmits, ComboboxContentProps } from 'reka-ui'
import { ComboboxContent, ComboboxPortal, ComboboxViewport, useForwardPropsEmits } from 'reka-ui'
import { computed, type HTMLAttributes } from 'vue'

import { cn } from '../../../lib/utils'

const properties = withDefaults(defineProps<ComboboxContentProps & { class?: HTMLAttributes['class'] }>(), {
  class: undefined,
  position: 'popper',
  align: 'center',
  sideOffset: 4,
})
const emits = defineEmits<ComboboxContentEmits>()

const delegatedProperties = computed(() => {
  const { class: _, ...delegated } = properties

  return delegated
})

const forwarded = useForwardPropsEmits(delegatedProperties, emits)
</script>

<template>
  <ComboboxPortal>
    <ComboboxContent v-bind="forwarded" :class="cn('atabix-combobox-content', properties.class)">
      <ComboboxViewport>
        <slot />
      </ComboboxViewport>
    </ComboboxContent>
  </ComboboxPortal>
</template>

<style lang="scss">
.atabix-combobox-content {
  @apply z-50;
  @apply w-[var(--reka-combobox-trigger-width)];
  @apply rounded-md;
  @apply border;
  @apply border-border;
  @apply bg-background;
  @apply text-foreground;
  @apply shadow-md;
  @apply outline-none;

  > div {
    @apply flex;
    @apply flex-col;
  }

  &[data-state='open'] {
    @apply animate-in;
  }
  &[data-state='closed'] {
    @apply animate-out;
  }

  &[data-side='bottom'] {
    @apply slide-in-from-top-2;
    @apply slide-out-to-top-2;
    @apply fade-out-0;
  }
  &[data-side='left'] {
    @apply slide-in-from-right-2;
    @apply slide-out-to-right-2;
    @apply fade-out-0;
  }

  &[data-side='right'] {
    @apply slide-in-from-left-2;
    @apply slide-out-to-left-2;
    @apply fade-in-0;
  }
  &[data-side='top'] {
    @apply slide-in-from-bottom-2;
    @apply slide-out-to-bottom-2;
    @apply fade-in-0;
  }
}
</style>
